import { useCallback, useState } from 'react';
import { baseUrl } from '../../../redux/api/fymAPI';
import { trackEvent } from '../../analytics/sml/analytics';
import { useUserToken } from '../../../redux/slices/appInfo/hooks';
import { downloadFile } from '../../../utils/qrCode/qrCodeSvg';
import { useMusic24Capabilities } from '../../music24/hooks/useMusic24Capabilities';
import { useUpgradePrompt } from '../../music24/hooks/useUpgradePrompt';
import { SMLUpgradeFeature } from '../../music24/config/music24PlanCapabilities';

/**
 * Pulls the campaign's sign-ups out of the server as a file.
 *
 * Not an RTK endpoint: the shared base query parses every response as JSON,
 * and this one is a CSV stream. So it is a plain fetch carrying the same
 * bearer token the query client sends, and the body becomes a blob the
 * browser saves.
 *
 * The name comes from the server's own `Content-Disposition`, so the file the
 * artist gets is the file the endpoint says it is.
 */
const FALLBACK_FILENAME = 'presaves.csv';

function filenameFromResponse(response: Response): string {
  const disposition = response.headers.get('Content-Disposition');
  const match = disposition === null ? null : /filename="([^"]+)"/.exec(disposition);
  return match?.[1] ?? FALLBACK_FILENAME;
}

export function useExportPresaves({ accountId, campaignUuid }: { accountId: number; campaignUuid: string | null }) {
  const userToken = useUserToken();
  const [isExporting, setIsExporting] = useState(false);
  const isLocked = !useMusic24Capabilities().canExportFans;
  const openUpgradePrompt = useUpgradePrompt();

  const exportPresaves = useCallback(() => {
    if (isLocked) {
      openUpgradePrompt(SMLUpgradeFeature.fanExport);
      return;
    }
    if (campaignUuid === null || userToken === null || userToken === undefined) {
      return;
    }
    setIsExporting(true);
    window
      .fetch(`${baseUrl}accounts/${accountId}/presaveCampaigns/${campaignUuid}/subscriptions.csv`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${userToken}` },
      })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`Could not export the pre-save list: ${response.status}`);
        }
        const blob = await response.blob();
        downloadFile(filenameFromResponse(response), URL.createObjectURL(blob), true);
        trackEvent('fans_exported', { kind: 'presaves' });
      })
      .catch(console.error)
      .finally(() => setIsExporting(false));
  }, [isLocked, openUpgradePrompt, accountId, campaignUuid, userToken]);

  return { exportPresaves, isExporting, canExport: campaignUuid !== null, isLocked };
}
